import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, fromEvent } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

export enum HighlightType {
    TEXT = 'text',
    ID = 'id',
    ANCHOR = 'anchor'
}

export interface HighlightItem {
    id: string;
    element: HTMLElement;
    text: string;
    index: number;
    type: HighlightType;
}

@Injectable({
    providedIn: 'root'
})
export class HighlightService implements OnDestroy {
    private highlightItems = new BehaviorSubject<HighlightItem[]>([]);
    private currentHighlightIndex = new BehaviorSubject<number>(-1);
    private highlightType: HighlightType = null;
    private highlightValue: string = '';
    private highlightActive = new BehaviorSubject<boolean>(false);
    private showOnlyMatched = new BehaviorSubject<boolean>(false);
    private destroy$ = new Subject<void>();
    private highlightAttempts = 0;
    private maxHighlightAttempts = 5;
    private routeSubscription: Subscription;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private ngZone: NgZone
    ) {
        console.log('HighlightService constructor çalıştı');
        
        // URL değişikliklerini izle
        this.routeSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            tap(() => {
                this.highlightAttempts = 0;
                this.detectHighlightParams();
            }),
            takeUntil(this.destroy$)
        ).subscribe();
        
        // Sayfa tam yüklendiğinde highlight işlemini tekrar dene
        // Bu özellikle doğrudan URL ile yüklenme durumları için önemli
        if (document.readyState === 'complete') {
            this.detectHighlightParams();
        } else {
            fromEvent(window, 'load').pipe(
                take(1),
                takeUntil(this.destroy$)
            ).subscribe(() => {
                this.detectHighlightParams();
            });
        }
    }

    /**
     * Service temizliği için
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    /**
     * URL'deki highlight parametrelerini algılar
     * Formatlar:
     * ?ht=text&hv=aranacak metin
     * ?ht=id&hv=elementId
     * ?ht=anchor&hv=anchorId
     */
    private detectHighlightParams(): void {
        // Mevcut route'un query params'lerini al
        this.route.queryParams.pipe(
            take(1)
        ).subscribe(params => {
            // Highlight tipi (ht) ve değeri (hv) parametrelerini al
            const highlightType = params['ht'];
            const highlightValue = params['hv'] || '';
        
            // Parametreleri kontrol et ve uygun tipe dönüştür
            let parsedType: HighlightType = null;
            if (highlightType) {
                switch (highlightType.toLowerCase()) {
                    case 'text':
                        parsedType = HighlightType.TEXT;
                        break;
                    case 'id':
                        parsedType = HighlightType.ID;
                        break;
                    case 'anchor':
                        parsedType = HighlightType.ANCHOR;
                        break;
                    default:
                        console.warn(`Geçersiz highlight tipi: ${highlightType}`);
                        break;
                }
            }
            
            // Önceki highlight tipi veya değeri değiştiyse, önce tam temizleme yap
            if (parsedType !== this.highlightType || highlightValue !== this.highlightValue) {
                console.log('Highlight tipi veya değeri değişti - Tüm DOM vurgulamaları temizleniyor');
                this.fullCleanup();
            }
            
            this.highlightType = parsedType;
            this.highlightValue = highlightValue;
            
            // Highlight parametresi değiştiyse, items'ları temizle
            if (parsedType && highlightValue) {
                this.highlightActive.next(true);
                
                // Farklı yükleme senaryoları için daha güvenilir bir yaklaşım
                this.ngZone.runOutsideAngular(() => {
                    this.scheduleHighlight();
                });
            } else {
                this.highlightActive.next(false);
            }
        });
    }
    
    /**
     * Highlight işlemini daha güvenilir bir şekilde zamanlar
     */
    private scheduleHighlight(): void {
        // Maksimum deneme sayısına ulaştıysak durdur
        if (this.highlightAttempts >= this.maxHighlightAttempts) {
            console.warn('Maksimum highlight deneme sayısına ulaşıldı');
            return;
        }
        
        this.highlightAttempts++;
        
        // İlk taramadan önce tüm vurgulama öğelerini sıfırla
        if (this.highlightAttempts === 1) {
            console.log('Vurgulama öğeleri sıfırlanıyor');
            // Tam temizleme yapmak için clearHighlights çağırılmasına gerek yok
            // detectHighlightParams'ta zaten fullCleanup çağrılacak
            this.highlightItems.next([]);
            this.currentHighlightIndex.next(-1);
        }
        
        // DOM içeriğinin yüklenmesi için kısa bir bekleme süresi
        setTimeout(() => {
            // Highlight işlemi gerçekleştir
            this.scanAndHighlightPage();
            
            const currentItems = this.getHighlightItems().length;
            console.log(`Tarama ${this.highlightAttempts}: Bulunan highlight öğe sayısı: ${currentItems}`);
            
            // Her durumda bir kaç kez tekrar tarama yapalım
            // DOM içeriğinin daha sonra yüklenmesi durumunu yakalamak için
            if (this.highlightAttempts < 3 || currentItems === 0) {
                console.log(`DOM'u tekrar taramak için yeniden deneniyor (${this.highlightAttempts}/${this.maxHighlightAttempts})`);
                this.scheduleHighlight();
            } else {
                // Son bir kez daha 1 saniye sonra tarayalım
                // Bu, Angular'dan sonra gelen ve geç yüklenen içeriği yakalamak için
                setTimeout(() => {
                    console.log('Son kontrol taraması yapılıyor...');
                    this.scanAndHighlightPage();
                    console.log(`Nihai vurgulama sayısı: ${this.getHighlightItems().length}`);
                }, 1000);
            }
        }, 300 * this.highlightAttempts); // Artan bekleme süresi
    }
    
    /**
     * Sayfayı tarayıp highlight değerine göre elementleri vurgular
     */
    private scanAndHighlightPage(): void {
        if (!this.highlightType || !this.highlightValue) {
            return;
        }
        
        console.log('Sayfa taranıyor ve vurgulanıyor:', { type: this.highlightType, value: this.highlightValue });
        
        switch (this.highlightType) {
            case HighlightType.TEXT:
                this.highlightTextInPage(this.highlightValue);
                break;
            case HighlightType.ID:
                this.highlightElementById(this.highlightValue);
                break;
            case HighlightType.ANCHOR:
                this.highlightElementByAnchor(this.highlightValue);
                break;
        }
    }
    
    /**
     * Sayfadaki metinleri tarayıp, eşleşenleri vurgular
     */
    private highlightTextInPage(searchText: string): void {
        if (!searchText) return;
        
        // İçerik düğümlerini seçmek için beyaz liste (sadece bu etiketlerin içeriğini tara)
        // BUTTON ekledik böylece buton içindeki metinler de taranacak
        // Daha fazla element tipi ekledik: A (bağlantılar), BUTTON (düğmeler), EM, STRONG, I, B (vurgu elementleri)
        const validTextTags = ['P', 'DIV', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'TD', 'TH', 'LABEL', 'BUTTON', 'A', 'EM', 'STRONG', 'I', 'B', 'SMALL', 'CODE'];
        
        // DOM'u gez ve metin içeriği olan elementleri bul
        const textNodes = [];
        const walkDOM = (node: Node) => {
            if (node.nodeType === 3) { // Text node
                const parent = node.parentElement;
                if (parent && validTextTags.includes(parent.tagName)) {
                    const text = node.textContent || '';
                    // Case insensitive kontrol - kesinlikle büyük/küçük harf duyarsız
                    if (text.toLowerCase().includes(searchText.toLowerCase())) {
                        textNodes.push(node);
                    }
                }
            } else if (node.nodeType === 1) { // Element node
                for (let i = 0; i < node.childNodes.length; i++) {
                    walkDOM(node.childNodes[i]);
                }
            }
        };
        
        walkDOM(document.body);
        console.log(`${textNodes.length} adet metin eşleşmesi bulundu`);
        
        // Tüm vurgulama öğelerini temizle, baştan başla
        this.highlightItems.next([]);
        this.currentHighlightIndex.next(-1);
        
        // Bulunan metin düğümlerini işaretle
        const showOnlyMatched = this.showOnlyMatched.getValue();
        console.log('Highlighting with showOnlyMatched:', showOnlyMatched);
        
        if (showOnlyMatched) {
            // SADECE EŞLEŞEN KELİME MODU
            // Metin içindeki kelimeyi vurgula
            textNodes.forEach((node) => {
                this.highlightTextNode(node as Text, searchText);
            });
        } else {
            // NORMAL MOD - TÜM ELEMENTİ VURGULA
            // Parent elementi vurgula (eski davranış)
            textNodes.forEach((node) => {
                const element = node.parentElement;
                if (element) {
                    this.addHighlightItem(element, searchText);
                }
            });
        }
        
        // İlk öğeye git
        if (this.getHighlightItems().length > 0) {
            this.setActiveHighlight(0);
        }
    }
    
    /**
     * Metin düğümünde sadece belirli bir kelimeyi vurgular
     * @param textNode Metin düğümü
     * @param searchText Aranacak metin
     */
    private highlightTextNode(textNode: Text, searchText: string): void {
        let text = textNode.textContent || '';
        const parent = textNode.parentElement;
        
        if (!parent || !text) return;
        
        // Önce parent element'in içinde zaten highlight edilmiş öğeler var mı kontrol et
        // Parent içinde rtls-highlight-word-only sınıfı varsa, önce onu temizlememiz gerekiyor
        const existingHighlights = parent.querySelectorAll('.rtls-highlight-word-only');
        if (existingHighlights.length > 0) {
            console.log(`Zaten vurgulanmış ${existingHighlights.length} adet kelime bulundu, temizleniyor...`);
            // Var olan vurgulamaları temizle ve orijinal metni yeniden oluştur
            this.clearParentTextHighlights(parent);
            
            // Temizleme sonrası yeni bir metin düğümü olabilir, onu bul
            // Temizlenmiş parent içinde yeni bir text düğümü bulalım
            for (let i = 0; i < parent.childNodes.length; i++) {
                const node = parent.childNodes[i];
                if (node.nodeType === 3 && (node.textContent || '').toLowerCase().includes(searchText.toLowerCase())) {
                    textNode = node as Text;
                    text = textNode.textContent || '';
                    break;
                }
            }
        }
        
        // Metin içerisinde aranan kelimeyi bul (kesinlikle case insensitive)
        // 'gi' flag'leri: 'g' global (tüm eşleşmeleri bul), 'i' case-insensitive (büyük/küçük harf duyarsız)
        const regex = new RegExp(this.escapeRegExp(searchText), 'gi');
        let match;
        let lastIndex = 0;
        const fragment = document.createDocumentFragment();
        
        // Metni parçalara böl ve sadece eşleşen kısımları vurgula
        while ((match = regex.exec(text)) !== null) {
            // Eşleşmeden önceki metni ekle
            if (match.index > lastIndex) {
                fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
            }
            
            // Eşleşen metni vurgula
            const span = document.createElement('span');
            span.textContent = match[0];
            span.classList.add('rtls-highlight');
            span.classList.add('rtls-highlight-word-only'); // Özel sınıf ekleme
            span.style.whiteSpace = 'normal';
            span.style.display = 'inline';
            
            // Bu span'i highlight itemlarına ekle
            this.addHighlightItem(span, searchText);
            
            fragment.appendChild(span);
            lastIndex = regex.lastIndex;
        }
        
        // Kalan metni ekle
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }
        
        // Orijinal metin düğümü yerine yeni oluşturulan içeriği yerleştir
        parent.replaceChild(fragment, textNode);
    }
    
    /**
     * Parent element içindeki tüm metin vurgulamalarını temizler ve orijinal text'e döndürür
     * @param parentElement Temizlenecek parent element
     */
    private clearParentTextHighlights(parentElement: HTMLElement): void {
        // Orijinal metni biriktirmek için metin içeriğini alıp birleştireceğiz
        let fullText = '';
        
        // Tüm child elementleri gez
        for (let i = 0; i < parentElement.childNodes.length; i++) {
            const node = parentElement.childNodes[i];
            if (node.nodeType === 3) { // Text node
                fullText += node.textContent || '';
            } else if (node.nodeType === 1) { // Element node
                const element = node as HTMLElement;
                if (element.classList && element.classList.contains('rtls-highlight-word-only')) {
                    fullText += element.textContent || '';
                } else {
                    // Highlight değilse içeriğini birleştir
                    fullText += element.textContent || '';
                }
            }
        }
        
        // Parent içindeki tüm node'ları temizle
        while (parentElement.firstChild) {
            parentElement.removeChild(parentElement.firstChild);
        }
        
        // Şimdi temiz metin node ekle
        parentElement.appendChild(document.createTextNode(fullText));
        
        // Highlight listesinden de bu parent'a ait öğeleri çıkar
        const items = this.getHighlightItems();
        const filteredItems = items.filter(item => {
            return !parentElement.contains(item.element) && 
                   item.element.parentElement !== parentElement;
        });
        this.highlightItems.next(filteredItems);
    }
    
    /**
     * Regex için özel karakterleri escape eder
     */
    private escapeRegExp(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    
    /**
     * ID'ye göre elementi vurgular
     */
    private highlightElementById(id: string): void {
        const element = document.getElementById(id);
        if (element) {
            this.addHighlightItem(element, id);
            this.setActiveHighlight(0);
        } else {
            console.warn(`'${id}' ID'sine sahip element bulunamadı`);
        }
    }
    
    /**
     * data-anchor özelliğine göre elementi vurgular
     */
    private highlightElementByAnchor(anchor: string): void {
        const elements = document.querySelectorAll(`[data-anchor="${anchor}"]`);
        if (elements.length > 0) {
            elements.forEach((element, index) => {
                this.addHighlightItem(element as HTMLElement, anchor);
            });
            this.setActiveHighlight(0);
        } else {
            console.warn(`'${anchor}' anchor'ına sahip element bulunamadı`);
        }
    }

    /**
     * Tüm highlight vurgulama işaretlerini temizler
     */
    public clearHighlights(): void {
        // Tüm vurgulama öğelerini temizle ve DOM'dan kaldır
        this.getHighlightItems().forEach(item => {
            if (item.element) {
                // CSS sınıflarını kaldır
                item.element.classList.remove('rtls-highlight', 'rtls-highlight-active', 'rtls-highlight-word-only');
                
                // Eğer bu bir "sadece kelime" vurgulaması ise ve parent element'e sahipse
                // DOM'dan tamamen kaldır ve yerine orijinal metin düğümü koy
                if (item.element.classList.contains('rtls-highlight-word-only') && item.element.parentElement) {
                    try {
                        // Bu denemede, kelime vurgulamalarını geri almaya çalışırız
                        // Özel durumdur, hata almaya açıktır, o yüzden try/catch blogu içinde
                        const textContent = item.element.textContent;
                        if (textContent) {
                            const textNode = document.createTextNode(textContent);
                            item.element.parentElement.replaceChild(textNode, item.element);
                        }
                    } catch (e) {
                        console.error('Highlight temizleme sırasında hata:', e);
                    }
                }
            }
        });
        
        this.highlightItems.next([]);
        this.currentHighlightIndex.next(-1);
        
        // DOM'daki highlight-* id'li elementleri de temizle
        document.querySelectorAll('[id^="highlight-"]').forEach(element => {
            element.classList.remove('rtls-highlight', 'rtls-highlight-active', 'rtls-highlight-word-only');
        });
    }
    
    /**
     * Tüm DOM'daki highlight işaretlerini ve vurgulamaları tamamen temizler
     * Kelime vurgulamalarını baştan sona tekrar yapılandırır
     */
    private fullCleanup(): void {
        console.log('Tam DOM temizliği gerçekleştiriliyor...');
        
        // 1. Önce BehaviorSubject'teki tüm vurgulama öğelerini temizle
        this.clearHighlights();
        
        // 2. Tüm DOM'da kalmış olabilecek rtls-highlight sınıfına sahip öğeleri bul
        const allHighlightedElements = document.querySelectorAll('.rtls-highlight');
        console.log(`DOM'da hala ${allHighlightedElements.length} adet vurgulama sınıfı taşıyan element bulundu, temizleniyor`);
        
        // 3. Tüm vurgulanmış öğeleri işle
        allHighlightedElements.forEach(highlightedElement => {
            try {
                // CSS sınıflarını kaldır
                highlightedElement.classList.remove('rtls-highlight', 'rtls-highlight-active', 'rtls-highlight-word-only');
                
                // Eğer bu bir "sadece kelime" vurgulaması ise, metni çıkar ve yerine düz metin koy
                if (highlightedElement.classList.contains('rtls-highlight-word-only') && highlightedElement.parentElement) {
                    const textContent = highlightedElement.textContent;
                    if (textContent) {
                        const textNode = document.createTextNode(textContent);
                        highlightedElement.parentElement.replaceChild(textNode, highlightedElement);
                    }
                }
            } catch (e) {
                console.error('DOM temizleme sırasında hata:', e);
            }
        });
        
        // 4. Tüm vurgulama özelliklerini sıfırla
        this.highlightItems.next([]);
        this.currentHighlightIndex.next(-1);
    }

    /**
     * Yeni bir elemanı highlight listesine ekler
     */
    public addHighlightItem(element: HTMLElement, text: string): void {
        if (!this.shouldHighlight(element, text)) {
            return;
        }

        const items = this.getHighlightItems();
        const isWordOnly = element.classList.contains('rtls-highlight-word-only');
        
        // Kelime vurgulaması ve normal vurgulama için farklı ID desenleri kullan
        const prefix = isWordOnly ? 'highlight-word-' : 'highlight-elem-';
        const id = `${prefix}${items.length}`;
        
        // Eğer element'in ID'si yoksa veya ID, highlight ile başlıyorsa değiştir
        // Bu, tekrar kullanım sırasında ID çakışmalarını önler
        if (!element.id || element.id.startsWith('highlight-')) {
            element.id = id;
        }
        
        element.classList.add('rtls-highlight');
        
        // Eleman kimliğinde modu belirt
        element.setAttribute('data-highlight-mode', isWordOnly ? 'word' : 'element');
        
        const newItem: HighlightItem = {
            id: element.id,
            element,
            text,
            index: items.length,
            type: this.highlightType
        };
        
        items.push(newItem);
        console.log(`Highlight öğesi eklendi. Toplam: ${items.length} öğe.`);
        this.highlightItems.next(items);
        
        // İlk eleman eklendiyse onu aktif yap
        if (items.length === 1) {
            this.setActiveHighlight(0);
        }
        
        element.addEventListener('click', () => {
            this.setActiveHighlight(newItem.index);
        });
    }

    /**
     * Eleman vurgulanmalı mı kontrol eder
     */
    private shouldHighlight(element: HTMLElement, text: string): boolean {
        // Highlight parametreleri eksikse vurgulama yapma
        if (!this.highlightType || !this.highlightValue) {
            return false;
        }
        
        // Highlight tipine göre kontrol yap
        switch (this.highlightType) {
            case HighlightType.TEXT:
                // Metin araması - contains (içerir) kontrolü yapılıyor, tam kelime eşleşmesi değil
                // Kesinlikle case insensitive (büyük/küçük harf duyarsız)
                return text.toLowerCase().includes(this.highlightValue.toLowerCase());
                
            case HighlightType.ID:
                // Doğrudan ID eşleşmesi
                return element.id === this.highlightValue;
                
            case HighlightType.ANCHOR:
                // Anchor attribute veya ID eşleşmesi
                return element.id === this.highlightValue || 
                       element.getAttribute('data-anchor') === this.highlightValue;
                
            default:
                return false;
        }
    }

    /**
     * Belirtilen indeksteki elemanı aktif highlight olarak işaretle
     * @param index Aktif yapılacak elemanın indeksi
     */
    public setActiveHighlight(index: number): void {
        const items = this.getHighlightItems();
        
        // Önceki aktif elemanın sınıfını kaldır
        const currentIndex = this.currentHighlightIndex.getValue();
        if (currentIndex >= 0 && currentIndex < items.length) {
            items[currentIndex].element.classList.remove('rtls-highlight-active');
        }
        
        // Yeni elemanı aktif yap
        if (index >= 0 && index < items.length) {
            items[index].element.classList.add('rtls-highlight-active');
            this.scrollToElement(items[index].element);
            this.currentHighlightIndex.next(index);
        }
    }

    /**
     * Bir elemana scroll yapar - güvenilir ama yumuşak bir geçiş sağlar
     */
    private scrollToElement(element: HTMLElement): void {
        // Önce temel scrollIntoView ile güvenilir bir şekilde elemana git
        // Bu yöntem daha güvenilir ve cross-browser uyumlu
        element.scrollIntoView({
            behavior: 'smooth', // Yumuşak geçiş
            block: 'center',    // Ekranın ortasına getir
            inline: 'nearest'   // Yatay konumunu korunması için
        });
        
        // Görsel vurgu için bir küçük animasyon efekti - dikkat çekmek için
        try {
            // Güvenlik için try-catch içinde
            const originalBackgroundColor = window.getComputedStyle(element).backgroundColor;
            
            // Geçiş efekti ayarla
            element.style.transition = 'background-color 0.5s ease-in-out';
            
            // Kısa bir parıltı efekti oluştur
            setTimeout(() => {
                element.style.backgroundColor = 'rgba(249, 115, 22, 0.4)'; // Biraz daha parlak turuncu
                
                setTimeout(() => {
                    // Orijinal renge geri dön - yumuşak geçiş ile
                    element.style.backgroundColor = originalBackgroundColor;
                    
                    // Bir süre sonra transition'u temizle
                    setTimeout(() => {
                        element.style.transition = '';
                        element.style.removeProperty('background-color'); // CSS sınıfının tekrar çalışması için inline style'i kaldır
                    }, 500);
                }, 300);
            }, 100);
        } catch (e) {
            console.error('Vurgulama animasyonu sırasında hata:', e);
        }
    }

    /**
     * Sonraki highlight elemanına geçer
     */
    public nextHighlight(): void {
        const items = this.getHighlightItems();
        if (items.length === 0) return;
        
        const currentIndex = this.currentHighlightIndex.getValue();
        const nextIndex = (currentIndex + 1) % items.length;
        this.setActiveHighlight(nextIndex);
    }

    /**
     * Önceki highlight elemanına geçer
     */
    public previousHighlight(): void {
        const items = this.getHighlightItems();
        if (items.length === 0) return;
        
        const currentIndex = this.currentHighlightIndex.getValue();
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        this.setActiveHighlight(prevIndex);
    }

    /**
     * Highlight olarak işaretlenmiş elemanları döndürür
     */
    public getHighlightItems(): HighlightItem[] {
        return this.highlightItems.getValue();
    }

    /**
     * Highlight olarak işaretlenmiş elemanları observable olarak döndürür
     */
    public highlightItems$(): Observable<HighlightItem[]> {
        return this.highlightItems.asObservable();
    }

    /**
     * Aktif highlight indeksini observable olarak döndürür
     */
    public currentHighlightIndex$(): Observable<number> {
        return this.currentHighlightIndex.asObservable();
    }

    /**
     * Highlight durumunun aktifliğini observable olarak döndürür
     */
    public highlightActive$(): Observable<boolean> {
        return this.highlightActive.asObservable();
    }
    
    /**
     * Sadece eşleşen kelimeyi gösterme/tüm satırı gösterme durumunu değiştirir
     */
    public toggleShowOnlyMatched(): void {
        const currentValue = this.showOnlyMatched.getValue();
        this.showOnlyMatched.next(!currentValue);
        
        // Vurgulamaları temizleyip yeniden oluştur
        console.log('Mode change - clearing and recreating highlights');
        
        // Önce DOM'daki tüm vurgulama span'larını ve CSS sınıflarını temizle
        this.clearHighlights();
        
        // Ekstra olarak, tüm highlight ile başlayan ID'lere sahip elementleri temizle
        document.querySelectorAll('[id^="highlight-word-"]').forEach(element => {
            try {
                if (element.parentElement) {
                    // Sadece kelime modu için eklenen span'ları temizle
                    const textContent = element.textContent;
                    if (textContent) {
                        const textNode = document.createTextNode(textContent);
                        element.parentElement.replaceChild(textNode, element);
                    }
                }
            } catch (e) {
                console.error('DOM temizleme hatası:', e);
            }
        });
        
        // Highlight tipine göre yeniden tarama ve vurgulama yap
        if (this.highlightType && this.highlightValue) {
            // İlk olarak tüm vurgulama öğelerini sıfırla
            this.highlightItems.next([]);
            this.currentHighlightIndex.next(-1);
            console.log('Toggle sonrası vurgulama öğeleri sıfırlandı');
            
         
                    setTimeout(() => {
                        this.scanAndHighlightPage();
                        console.log(`Nihai tarama sonrası öğe sayısı: ${this.getHighlightItems().length}`);
                    }, 500);
        }
    }
    
    /**
     * Sadece eşleşen kelimeyi gösterme durumunu observable olarak döndürür
     */
    public showOnlyMatched$(): Observable<boolean> {
        return this.showOnlyMatched.asObservable();
    }
    
    /**
     * Highlight durumunu ve vurgulanan elementleri yeniden değerlendir
     */
    private refreshHighlightStyles(): void {
        console.log('Show only matched word:', this.showOnlyMatched.getValue());
        
        // Highlight modunu değiştirdiğimizde tüm vurgulamaları temizleyip yeniden oluşturmak en iyisi
        if (this.highlightType === HighlightType.TEXT && this.highlightValue) {
            this.clearHighlights();
            this.scanAndHighlightPage();
        }
    }

    /**
     * URL'deki highlight parametresini değiştirir
     * @param type Highlight tipi (text, id, anchor)
     * @param value Aranacak değer
     */
    public setHighlightParams(type: HighlightType, value: string): void {
        const queryParams: any = {};
        
        if (type && value) {
            queryParams.ht = type;
            queryParams.hv = value;
        }
        
        this.router.navigate([], {
            queryParams: queryParams,
            queryParamsHandling: 'merge'
        });
    }
    
    /**
     * Tüm highlight parametrelerini temizler
     */
    public clearHighlightParams(): void {
        // ht ve hv parametrelerini URL'den kaldır
        const queryParams = { ...this.router.routerState.snapshot.root.queryParams };
        delete queryParams['ht'];
        delete queryParams['hv'];
        
        this.router.navigate([], {
            queryParams: queryParams
        });
    }
}
